import {
  DEFAULT_ELEVATION_PROFILE_TOOL_MODE,
  deleteElevationProfileAtom,
  ElevationProfile,
  elevationProfileProgressAtom,
  elevationProfilesAtom,
  elevationProfileToolModeAtom,
  hoveredProfileChainageAtom,
  renameElevationProfileAtom,
  updateElevationProfileAtom,
} from '@/atoms';
import { layersAtom } from '@/atoms/layers.atom';
import { pointCloudTargetsAtom } from '@/atoms/viewer.atom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Slider } from '@/components/ui/slider';
import { downloadFile } from '@/lib/download';
import {
  computeElevationProfile,
  ElevationProfileAbortError,
} from '@/lib/elevationProfile/computeProfile';
import { buildSegments, totalLength as polylineTotalLength } from '@/lib/elevationProfile/polylineMath';
import { profileToCsv, profileToGeoJson } from '@/lib/elevationProfile/profileExport';
import { execute } from '@/graphql/execute';
import { getProjectQuery } from '@/queries/project.query';
import { useQuery } from '@tanstack/react-query';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { Bar, Line, LinePath } from '@visx/shape';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Download } from '@mynaui/icons-react';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const MIN_BUFFER = 0.1;
const MAX_BUFFER = 10;
const BUFFER_STEP = 0.1;

interface PanelProps {
  projectId: string;
}

/** Binary-search the leftmost sample index whose chainage ≥ `target`. */
const bisectChainageLeft = (
  samples: ReadonlyArray<{ chainage: number }>,
  target: number,
): number => {
  let lo = 0;
  let hi = samples.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (samples[mid].chainage < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

/**
 * Right-side sheet panel that opens once the user has finished drawing a
 * polyline (`elevationProfileToolModeAtom.phase === 'panel'`). Drives the
 * sampling compute, displays the profile chart, and persists results back
 * to the per-project Jotai atom (which is localStorage-backed).
 */
export const ElevationProfilePanel = ({ projectId }: PanelProps) => {
  const { t } = useTranslation('project');
  const [mode, setMode] = useAtom(elevationProfileToolModeAtom);
  const profiles = useAtomValue(elevationProfilesAtom(projectId));
  const updateProfile = useSetAtom(updateElevationProfileAtom(projectId));
  const renameProfile = useSetAtom(renameElevationProfileAtom(projectId));
  const deleteProfile = useSetAtom(deleteElevationProfileAtom(projectId));
  const [progress, setProgress] = useAtom(elevationProfileProgressAtom);
  const setHover = useSetAtom(hoveredProfileChainageAtom);
  const layers = useAtomValue(layersAtom);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);

  // Project SRID — written into the GeoJSON `crs` property so external
  // tools (QGIS / GDAL) reproject correctly. Same fetch pattern as
  // StockpileComputePanel.
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });
  const projectSrid: number | undefined = projectData?.project?.srid;

  const open = mode.phase === 'panel' && mode.activeId !== null;
  const profile = useMemo(
    () => (mode.activeId ? profiles.find((p) => p.id === mode.activeId) ?? null : null),
    [mode.activeId, profiles],
  );

  const [computing, setComputing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  // Local slider state so dragging doesn't write to localStorage (and re-render
  // the rest of the app) on every tick. The atom updates in `handleBufferCommit`
  // when the user releases the thumb.
  const [bufferDraft, setBufferDraft] = useState(MIN_BUFFER);

  // Reset form-side state every time the panel opens for a new profile.
  useEffect(() => {
    if (open && profile) {
      setNameDraft(profile.name);
      setBufferDraft(profile.bufferDistance);
      setComputing(false);
      setProgress(null);
      abortRef.current?.abort();
      abortRef.current = null;
    }
    if (!open) {
      setHover(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profile?.id]);

  // External buffer changes (e.g. another panel re-open with the same profile)
  // re-sync the draft.
  useEffect(() => {
    if (profile) setBufferDraft(profile.bufferDistance);
  }, [profile?.bufferDistance]);

  const pcoLayers = useMemo(() => layers.filter((l) => l.type === 'POINTCLOUD'), [layers]);
  const sourcePco = profile?.sourceLayerId
    ? pcoTargets.find((p) => (p.userData as any)?.layerId === profile.sourceLayerId)
    : null;
  // Live polyline length — independent of sampling, so we can show it as soon
  // as the panel opens (the persisted `profile.totalLength` only fills in
  // after the first compute).
  const liveLength = useMemo(
    () => (profile ? polylineTotalLength(buildSegments(profile.polylinePoints)) : 0),
    [profile?.polylinePoints],
  );

  const handleClose = () => {
    abortRef.current?.abort();
    setProgress(null);
    setMode({ ...DEFAULT_ELEVATION_PROFILE_TOOL_MODE });
    setHover(null);
  };

  const handleCompute = async () => {
    if (!profile) return;
    if (!sourcePco) {
      toast.error(t('elevationProfile.errorNoSourceLayer'));
      return;
    }
    const ac = new AbortController();
    abortRef.current = ac;
    setComputing(true);
    setProgress({ phase: 'loading', done: 0, total: 0 });
    try {
      const result = await computeElevationProfile({
        polyline: profile.polylinePoints,
        buffer: profile.bufferDistance,
        pco: sourcePco,
        signal: ac.signal,
        onProgress: (phase, done, total) => {
          setProgress({ phase, done, total });
        },
      });
      updateProfile({
        id: profile.id,
        samples: result.samples,
        totalLength: result.totalLength,
        pointCount: result.rawPointCount,
      });
      if (result.samples.length === 0) {
        toast.warning(t('elevationProfile.warnNoPointsInBuffer'));
      }
    } catch (err: any) {
      if (err instanceof ElevationProfileAbortError) {
        toast.info(t('elevationProfile.computeCanceled'));
      } else {
        toast.error(err?.message ?? t('elevationProfile.computeFailed'));
      }
    } finally {
      setComputing(false);
      setProgress(null);
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  const handleSourceLayerChange = (layerId: string) => {
    if (!profile) return;
    // Don't wipe samples — the user may want to compare against the previous
    // result, and "Recompute" is one click away. Mirrors StockpileComputePanel.
    updateProfile({ id: profile.id, sourceLayerId: layerId });
  };

  const handleBufferCommit = (v: number) => {
    if (!profile) return;
    updateProfile({ id: profile.id, bufferDistance: v });
  };

  const handleRename = () => {
    if (!profile) return;
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== profile.name) {
      renameProfile({ id: profile.id, name: trimmed });
    }
  };

  const handleDelete = () => {
    if (!profile) return;
    deleteProfile(profile.id);
    handleClose();
  };

  const handleExportCsv = () => {
    if (!profile || profile.samples.length === 0) return;
    const csv = profileToCsv(profile, {
      sourceLayerName: sourceLayerName ?? undefined,
      srid: projectSrid,
    });
    const filename = `${profile.name || 'elevation-profile'}.csv`;
    downloadFile(csv, filename, 'text/csv');
    toast.success(t('elevationProfile.csvExportSuccess'));
  };

  const handleExportGeojson = () => {
    if (!profile || profile.samples.length === 0) return;
    const fc = profileToGeoJson(profile, {
      sourceLayerName: sourceLayerName ?? undefined,
      srid: projectSrid,
    });
    const filename = `${profile.name || 'elevation-profile'}.geojson`;
    // `application/geo+json` is the IANA-registered media type for GeoJSON
    // (RFC 7946); QGIS / browsers handle it correctly. Pretty-printed for
    // hand-readability.
    downloadFile(JSON.stringify(fc, null, 2), filename, 'application/geo+json');
    toast.success(t('elevationProfile.geojsonExportSuccess'));
  };

  const hasSamples = !!profile && profile.samples.length > 0;
  const sourceLayerName =
    profile && profile.sourceLayerId
      ? pcoLayers.find((l) => l.id === profile.sourceLayerId)?.name ?? null
      : null;

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}>
      <SheetContent side='right' className='sm:max-w-lg w-full flex flex-col gap-4 overflow-y-auto'>
        <SheetHeader>
          <SheetTitle>{t('elevationProfile.panelTitle')}</SheetTitle>
          <SheetDescription>{t('elevationProfile.panelDescription')}</SheetDescription>
        </SheetHeader>

        {!profile ? null : (
          <div className='flex flex-col gap-4 px-4 pb-6'>
            {/* Name */}
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='elev-name'>{t('elevationProfile.fieldName')}</Label>
              <Input
                id='elev-name'
                value={nameDraft}
                placeholder={t('elevationProfile.fieldNamePlaceholder')}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={handleRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
              />
            </div>

            {/* Source layer */}
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='elev-source'>{t('elevationProfile.sourceLayer')}</Label>
              <Select
                value={profile.sourceLayerId ?? undefined}
                onValueChange={handleSourceLayerChange}
                disabled={computing}>
                <SelectTrigger id='elev-source'>
                  <SelectValue placeholder={t('elevationProfile.sourceLayerPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {pcoLayers.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-slate-600 dark:text-stone-400'>
                {t('elevationProfile.sourceLayerHelp')}
              </p>
            </div>

            {/* Buffer width slider */}
            <div className='flex flex-col gap-1.5'>
              <div className='flex items-center justify-between'>
                <Label>{t('elevationProfile.bufferWidth')}</Label>
                <span className='text-xs font-mono text-slate-700 dark:text-stone-200'>
                  {bufferDraft.toFixed(2)} m
                </span>
              </div>
              <Slider
                min={MIN_BUFFER}
                max={MAX_BUFFER}
                step={BUFFER_STEP}
                value={[bufferDraft]}
                onValueChange={(v) => setBufferDraft(v[0])}
                onValueCommit={(v) => handleBufferCommit(v[0])}
                disabled={computing}
              />
              <p className='text-xs text-slate-600 dark:text-stone-400'>
                {t('elevationProfile.bufferWidthHelp')}
              </p>
            </div>

            {/* Polyline summary */}
            <section className='flex flex-col gap-1 text-sm border-t border-slate-200 dark:border-stone-700 pt-3'>
              <div className='flex justify-between'>
                <span className='text-slate-600 dark:text-stone-400'>
                  {t('elevationProfile.vertexCount')}
                </span>
                <span className='font-medium'>{profile.polylinePoints.length}</span>
              </div>
              {liveLength > 0 && (
                <div className='flex justify-between'>
                  <span className='text-slate-600 dark:text-stone-400'>
                    {t('elevationProfile.totalLength')}
                  </span>
                  <span className='font-medium'>{liveLength.toFixed(2)} m</span>
                </div>
              )}
              {profile.pointCount > 0 && (
                <div className='flex justify-between'>
                  <span className='text-slate-600 dark:text-stone-400'>
                    {t('elevationProfile.pointCount')}
                  </span>
                  <span className='font-medium'>{profile.pointCount.toLocaleString()}</span>
                </div>
              )}
            </section>

            {/* Compute / progress */}
            <section className='flex flex-col gap-2'>
              {!computing && (
                <Button
                  onClick={handleCompute}
                  disabled={!profile.sourceLayerId || !sourcePco}>
                  {hasSamples
                    ? t('elevationProfile.recompute')
                    : t('elevationProfile.compute')}
                </Button>
              )}
              {/* Inline help that explains the disabled-Compute states. The
                  button alone gives no signal as to why it can't be clicked,
                  which is the #1 source of UX confusion in tools that
                  require multiple inputs (a layer, geometry, etc.). */}
              {!computing && !profile.sourceLayerId && (
                <p className='text-xs text-slate-500 dark:text-stone-400'>
                  {t('elevationProfile.helpPickSource')}
                </p>
              )}
              {!computing && profile.sourceLayerId && !sourcePco && (
                <p className='text-xs text-amber-600 dark:text-amber-400'>
                  {t('elevationProfile.helpEnableLayer')}
                </p>
              )}
              {computing && progress && (
                <div className='flex flex-col gap-2'>
                  <Progress
                    value={
                      progress.total > 0
                        ? progress.phase === 'loading'
                          ? (progress.done / progress.total) * 40
                          : 40 + (progress.done / progress.total) * 60
                        : 0
                    }
                  />
                  <div className='flex items-center justify-between text-xs text-slate-600 dark:text-stone-400'>
                    <span>
                      {progress.phase === 'loading'
                        ? t('elevationProfile.loadingNodes', {
                            done: progress.done,
                            total: progress.total,
                          })
                        : t('elevationProfile.sampling', {
                            done: progress.done.toLocaleString(),
                            total: progress.total.toLocaleString(),
                          })}
                    </span>
                    <button
                      type='button'
                      className='text-red-600 hover:underline'
                      onClick={handleCancel}>
                      {t('elevationProfile.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Chart */}
            {hasSamples && (
              <section className='flex flex-col gap-2 border-t border-slate-200 dark:border-stone-700 pt-3'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-sm font-semibold text-slate-800 dark:text-stone-100'>
                    {t('elevationProfile.chartTitle')}
                  </h3>
                  {/* Split button: main button does the default action (CSV
                      export), the chevron opens the format picker. Mirrors
                      the attribute-table "Download all features" control so
                      the export affordance feels consistent across the app. */}
                  <div className='flex'>
                    <Button
                      size='sm'
                      variant='outline'
                      className='rounded-r-none'
                      onClick={handleExportCsv}>
                      <Download className='size-4' />
                      {t('elevationProfile.download')}
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size='sm'
                          variant='outline'
                          className='rounded-l-none border-l-0 px-2!'>
                          <ChevronDown className='size-4' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        <DropdownMenuItem onClick={handleExportCsv}>
                          {t('elevationProfile.downloadCsv')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleExportGeojson}>
                          {t('elevationProfile.downloadGeojson')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className='w-full h-[260px]'>
                  <ParentSize>
                    {({ width, height }) =>
                      width > 0 && height > 0 ? (
                        <ProfileChart
                          width={width}
                          height={height}
                          profile={profile}
                          distanceLabel={t('elevationProfile.distance')}
                          elevationLabel={t('elevationProfile.elevation')}
                          onHover={(c) =>
                            setHover(c == null ? null : { profileId: profile.id, chainage: c })
                          }
                        />
                      ) : null
                    }
                  </ParentSize>
                </div>
                {sourceLayerName && (
                  <p className='text-xs text-slate-500 dark:text-stone-400'>
                    {t('elevationProfile.sourceLayerNote', { name: sourceLayerName })}
                  </p>
                )}
              </section>
            )}

            {/* Footer actions */}
            <div className='flex gap-2 mt-2'>
              <Button variant='destructive' onClick={handleDelete}>
                {t('elevationProfile.delete')}
              </Button>
              <Button variant='ghost' className='ml-auto' onClick={handleClose}>
                {t('elevationProfile.close')}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// Margins leave room for axis labels — bottom and left are larger than the
// stockpile-style chart because we render explicit "Distance (m)" /
// "Elevation (m)" text alongside the tick numbers.
const MARGIN = { top: 12, right: 18, bottom: 44, left: 56 };

const ProfileChart = ({
  width,
  height,
  profile,
  distanceLabel,
  elevationLabel,
  onHover,
}: {
  width: number;
  height: number;
  profile: ElevationProfile;
  distanceLabel: string;
  elevationLabel: string;
  onHover: (chainage: number | null) => void;
}) => {
  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, height - MARGIN.top - MARGIN.bottom);
  const samples = profile.samples;

  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, profile.totalLength || 1],
        range: [0, innerWidth],
        nice: true,
      }),
    [profile.totalLength, innerWidth],
  );
  const yScale = useMemo(() => {
    if (samples.length === 0) {
      return scaleLinear<number>({ domain: [0, 1], range: [innerHeight, 0], nice: true });
    }
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const s of samples) {
      if (s.elevation < minZ) minZ = s.elevation;
      if (s.elevation > maxZ) maxZ = s.elevation;
    }
    const pad = Math.max(0.1, (maxZ - minZ) * 0.05);
    return scaleLinear<number>({
      domain: [minZ - pad, maxZ + pad],
      range: [innerHeight, 0],
      nice: true,
    });
  }, [samples, innerHeight]);

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<{ chainage: number; elevation: number }>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ detectBounds: true, scroll: true });

  const handleMove = (e: React.MouseEvent<SVGRectElement> | React.TouchEvent<SVGRectElement>) => {
    if (samples.length === 0) return;
    const pt = localPoint(e);
    if (!pt) return;
    const xInChart = pt.x - MARGIN.left;
    const chainage = xScale.invert(xInChart);
    const idx = bisectChainageLeft(samples, chainage);
    const a = samples[idx - 1];
    const b = samples[idx] ?? a;
    const sample = !a ? b : !b ? a : Math.abs(chainage - a.chainage) < Math.abs(b.chainage - chainage) ? a : b;
    if (!sample) return;
    showTooltip({
      tooltipData: sample,
      tooltipLeft: MARGIN.left + xScale(sample.chainage),
      tooltipTop: MARGIN.top + yScale(sample.elevation),
    });
    onHover(sample.chainage);
  };

  const handleLeave = () => {
    hideTooltip();
    onHover(null);
  };

  return (
    // `text-slate-700 dark:text-stone-300` resolves `currentColor` for
    // axis ticks, gridlines, and the hover crosshair below. Without this,
    // currentColor inherits whatever ambient color the panel's wrapper sets,
    // which on the Sheet is near-black in light mode (OK) but invisible in
    // dark mode.
    <div ref={containerRef} className='relative w-full h-full text-slate-700 dark:text-stone-300'>
      <svg width={width} height={height}>
        <rect width={width} height={height} fill='transparent' />
        <Group left={MARGIN.left} top={MARGIN.top}>
          {/* Lightweight grid — visx doesn't ship @visx/grid in our deps,
              and rendering ticks straight off the scales is plenty for a
              profile chart. */}
          {yScale.ticks(5).map((tick) => (
            <line
              key={`gy-${tick}`}
              x1={0}
              x2={innerWidth}
              y1={yScale(tick)}
              y2={yScale(tick)}
              stroke='currentColor'
              strokeOpacity={0.1}
              pointerEvents='none'
            />
          ))}
          {xScale.ticks(Math.max(2, Math.floor(innerWidth / 80))).map((tick) => (
            <line
              key={`gx-${tick}`}
              x1={xScale(tick)}
              x2={xScale(tick)}
              y1={0}
              y2={innerHeight}
              stroke='currentColor'
              strokeOpacity={0.1}
              pointerEvents='none'
            />
          ))}
          <AxisLeft
            scale={yScale}
            numTicks={5}
            stroke='currentColor'
            tickStroke='currentColor'
            label={elevationLabel}
            labelOffset={32}
            labelProps={{
              fill: 'currentColor',
              fontSize: 10,
              textAnchor: 'middle',
            }}
            tickLabelProps={() => ({
              fill: 'currentColor',
              fontSize: 10,
              textAnchor: 'end',
              dx: '-0.25em',
              dy: '0.25em',
            })}
          />
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            numTicks={Math.max(2, Math.floor(innerWidth / 80))}
            stroke='currentColor'
            tickStroke='currentColor'
            label={distanceLabel}
            labelOffset={14}
            labelProps={{
              fill: 'currentColor',
              fontSize: 10,
              textAnchor: 'middle',
            }}
            tickLabelProps={() => ({
              fill: 'currentColor',
              fontSize: 10,
              textAnchor: 'middle',
            })}
          />
          <LinePath
            data={samples}
            x={(d) => xScale(d.chainage)}
            y={(d) => yScale(d.elevation)}
            stroke='#ef4444'
            strokeWidth={1.5}
            curve={curveMonotoneX}
          />
          {tooltipOpen && tooltipData && (
            <>
              <Line
                from={{ x: xScale(tooltipData.chainage), y: 0 }}
                to={{ x: xScale(tooltipData.chainage), y: innerHeight }}
                stroke='currentColor'
                strokeOpacity={0.4}
                strokeDasharray='2,2'
                pointerEvents='none'
              />
              <circle
                cx={xScale(tooltipData.chainage)}
                cy={yScale(tooltipData.elevation)}
                r={4}
                fill='#ef4444'
                stroke='white'
                strokeWidth={1.5}
                pointerEvents='none'
              />
            </>
          )}
          <Bar
            x={0}
            y={0}
            width={innerWidth}
            height={innerHeight}
            fill='transparent'
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            onTouchMove={handleMove}
            onTouchEnd={handleLeave}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          className='!bg-slate-800 !text-white !rounded-md !px-2 !py-1 !text-xs !shadow-lg pointer-events-none'>
          <div>{tooltipData.chainage.toFixed(2)} m</div>
          <div className='text-slate-300'>{tooltipData.elevation.toFixed(2)} m</div>
        </TooltipInPortal>
      )}
    </div>
  );
};
